import React, { FC, useEffect, useState } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";

import { NoDataMessage, SpinLoader } from "@/components/atoms";
import { TabPanel, Tabs } from "@/components/molecules";
import { SeeAllButton } from "@/components/pages/Dashboard/shared";
import { StyledDashboardTab, StyledDashboardTabsPanel } from "@/components/pages/Dashboard/styled";
import { BrokerState } from "@/shared/domain";
import { useNavigation } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import ClaimItem from "../components/ClaimItem/ClaimItem";
import ClaimTable from "../components/ClaimTable/ClaimTable";
import { ClosedClaimItem } from "../components/ClosedClaimItem/ClosedClaimItem";
import { useClaimsQueries } from "../useClaimsQueries";

enum TabIndex {
    ON_GOING = 0,
    CLOSED = 1,
}

export const MAX_PRIVATE_CLOSED_ITEMS = 4;
export const MAX_PRIVATE_ONGOING_ITEMS = 3;
export const MAX_PRO_ITEMS = 8;

const ClaimPanel: FC = () => {
    const { t } = useTranslation();
    const { navToClaims } = useNavigation();
    const [selectedTab, setSelectedTab] = useState(TabIndex.ON_GOING);

    const { tableMode: renderAsTable, useClaimsPageQuery } = useClaimsQueries({});

    const {
        isFetched: isFetchedClosed,
        isFetching: isFetchingClosed,
        result: claimsClosed,
        orderBy: orderByClosed,
        handleSort: handleSortClosed,
    } = useClaimsPageQuery({ defaultCategory: BrokerState.CLOSED });

    const {
        isFetched: isFetchedOnGoing,
        isFetching: isFetchingOnGoing,
        result: claimsOnGoing,
        orderBy: orderOnGoing,
        handleSort: handleSortOnGoing,
    } = useClaimsPageQuery({ defaultCategory: BrokerState.ON_GOING });

    const maxOnGoingItems = renderAsTable ? MAX_PRO_ITEMS : MAX_PRIVATE_ONGOING_ITEMS;
    const maxClosedItems = renderAsTable ? MAX_PRO_ITEMS : MAX_PRIVATE_CLOSED_ITEMS;
    const displayMoreOnGoing = claimsOnGoing.length > maxOnGoingItems;
    const displayMoreClosed = claimsClosed.length > maxClosedItems;

    const renderOnGoingClaims = () => {
        if (claimsOnGoing.length === 0) {
            return <NoDataMessage>{t("Common.NoClaims")}</NoDataMessage>;
        }
        const items = claimsOnGoing.first(maxOnGoingItems);
        if (renderAsTable) {
            return <ClaimTable claims={items} orderBy={orderOnGoing} handleSort={handleSortOnGoing} />;
        } else {
            return items.map((item) => (
                <ClaimItem claim={item} key={`${item.id}-${item.objectType.id}-${item.insurer.fsma}`} />
            ));
        }
    };

    const renderClosedClaims = () => {
        if (claimsClosed.length === 0) {
            return <NoDataMessage>{t("Common.NoClaims")}</NoDataMessage>;
        }
        const items = claimsClosed.first(maxClosedItems);
        if (renderAsTable) {
            return <ClaimTable claims={items} orderBy={orderByClosed} handleSort={handleSortClosed} />;
        } else {
            return items.map((item) => (
                <ClosedClaimItem claim={item} key={`${item.id}-${item.objectType.id}-${item.insurer.fsma}`} />
            ));
        }
    };

    const displayTabs = claimsOnGoing.length > 0 && claimsClosed.length > 0;
    const displayOnGoingPanel = claimsOnGoing.length > 0 || (claimsOnGoing.length === 0 && claimsClosed.length === 0);
    const displayClosedPanel = claimsClosed.length > 0;

    const handleSeeMoreClaims = () => {
        let filter = BrokerState.CLOSED;
        if (displayTabs) {
            filter = selectedTab === TabIndex.CLOSED ? BrokerState.CLOSED : BrokerState.ON_GOING;
        } else if (displayOnGoingPanel) {
            filter = BrokerState.ON_GOING;
        }
        log.info("See more claims", filter);

        return navToClaims(filter);
    };

    useEffect(() => {
        if (isFetchingOnGoing && isFetchingClosed && displayTabs) {
            tagManager.pushViewItemListEvent("claim", "Claims", true);
        }
    }, [claimsOnGoing, claimsClosed]);

    if (isFetchingOnGoing || isFetchingClosed || !isFetchedOnGoing || !isFetchedClosed) {
        return <SpinLoader />;
    }

    return (
        <div>
            {displayTabs && (
                <Tabs value={selectedTab} onChange={(_event, tab) => setSelectedTab(tab)}>
                    <StyledDashboardTab label={t("Common.InProgress")} data-testid="claim_panel-tab-going" />
                    {claimsClosed.length > 0 && (
                        <StyledDashboardTab label={t("Common.Closed")} data-testid="claim_panel-tab-closed" />
                    )}
                </Tabs>
            )}
            <StyledDashboardTabsPanel $withoutTabs={!displayTabs}>
                {displayOnGoingPanel && (
                    <TabPanel
                        key={TabIndex.ON_GOING}
                        data-testid="claim_panel-panel-ongoing"
                        value={displayTabs ? selectedTab : TabIndex.ON_GOING}
                        index={TabIndex.ON_GOING}
                    >
                        {renderOnGoingClaims()}
                        {displayMoreOnGoing && (
                            <SeeAllButton onClick={handleSeeMoreClaims} dataTestId="see_more_button-button-claim" />
                        )}
                    </TabPanel>
                )}
                {displayClosedPanel && (
                    <TabPanel
                        key={TabIndex.CLOSED}
                        data-testid="claim_panel-panel-closed"
                        value={displayTabs ? selectedTab : TabIndex.CLOSED}
                        index={TabIndex.CLOSED}
                    >
                        {renderClosedClaims()}
                        {displayMoreClosed && (
                            <SeeAllButton onClick={handleSeeMoreClaims} dataTestId="see_more_button-button-claim" />
                        )}
                    </TabPanel>
                )}
            </StyledDashboardTabsPanel>
        </div>
    );
};

export default ClaimPanel;
